import { Theme } from "@mui/material";
import { useRef, useState } from "react";
import { ReferenceDot, ReferenceLine } from "recharts";

export const useChartNavigation = <T,>({
  data,
  dataKey,
  seriesType,
  seriesColor,
  theme,
  getX,
}: {
  data: T[] | undefined;
  dataKey: keyof T;
  seriesType: "line" | "bar";
  seriesColor: string;
  theme: Theme;
  getX: (record: T) => string | number;
}) => {
  const [lockedIndex, setLockedIndex] = useState<number | null>(null);
  const hoverIndexRef = useRef<number | null>(null);

  const chartEventHandlers = {
    onMouseMove: (state: unknown) => {
      const idx = (state as { activeTooltipIndex?: number })
        .activeTooltipIndex;
      if (idx !== undefined) {
        hoverIndexRef.current = idx;
        setLockedIndex(null);
      }
    },
    onMouseLeave: () => {
      hoverIndexRef.current = null;
      setLockedIndex(null);
    },
  };

  const tooltipProps = {
    cursor:
      lockedIndex !== null
        ? false
        : {
            strokeDasharray: "5 5",
            stroke: theme.palette.divider,
            strokeWidth: 2,
          },
    wrapperStyle:
      lockedIndex !== null
        ? { visibility: "visible" as const, pointerEvents: "auto" as const }
        : { pointerEvents: "auto" as const },
  };

  const navigationProps = {
    lockedIndex,
    allData: data,
    onPrev: () => {
      const base = lockedIndex ?? hoverIndexRef.current ?? 0;
      setLockedIndex(Math.max(0, base - 1));
    },
    onNext: () => {
      const base = lockedIndex ?? hoverIndexRef.current ?? 0;
      setLockedIndex(Math.min((data?.length ?? 1) - 1, base + 1));
    },
  };

  const renderIndicators = () => {
    if (lockedIndex === null || !data) return null;
    const record = data[lockedIndex];
    const x = getX(record);
    return (
      <>
        <ReferenceLine
          x={x}
          stroke={theme.palette.divider}
          strokeDasharray="5 5"
          strokeWidth={2}
        />
        {seriesType === "line" && record[dataKey] != null && (
          <ReferenceDot
            x={x}
            y={record[dataKey] as number}
            r={4}
            isFront
            shape={(props: { cx?: number; cy?: number }) => (
              <circle
                cx={props.cx}
                cy={props.cy}
                r={4}
                fill={seriesColor}
                stroke="#fff"
                strokeWidth={2}
              />
            )}
          />
        )}
      </>
    );
  };

  return {
    lockedIndex,
    chartEventHandlers,
    tooltipProps,
    navigationProps,
    renderIndicators,
  };
};
